import { useIntl } from 'react-intl';
import { WarningTwoTone } from '@ant-design/icons';
import {
  useCustomerGeneral,
  useUpdateCustomerLGPD,
  useUpdateCustomerShowLGPD,
} from '@/core/domain/people/Customers/Customers.hooks';
import { Button, Checkbox, Modal, notification, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useDisclosure } from '@/hooks';
import { SaveButton } from '@/components/_common';

interface LGPDTermModal {
  customerId?: number;
  onCallback?: (type: 'close' | 'cancel' | 'confirm') => void;
  open: boolean;
  onClose: () => void;
  setAlert?: (alert: boolean) => void;
}

export default function DirectSaleLGPDTermModal({
  customerId,
  onCallback,
  open,
  onClose,
  setAlert,
}: LGPDTermModal) {
  const intl = useIntl().formatMessage;
  const { data, isFetching } = useCustomerGeneral(customerId);
  const [modalShow, setModalShow] = useDisclosure(false);
  const [remind, setRemind] = useState(false);
  const customerLgpd = useUpdateCustomerLGPD();
  const customerShowLgpd = useUpdateCustomerShowLGPD();

  useEffect(() => {
    if (data && open && !data.showLGPD && !data.enableLGPD) {
      setModalShow.open();
    }
  }, [isFetching, open]);

  const handleEnableLgpd = async (enableLgpd: boolean | null) => {
    try {
      if (customerId) {
        await customerLgpd
          .mutateAsync({ id: customerId, enableLgpd })
          .then(() => {
            setModalShow.close();
            onClose();
          });

        if (onCallback) {
          onCallback('confirm');
        }
      }
    } catch {}
  };

  const handleDontShowAgain = async () => {
    try {
      if (customerId) {
        await customerShowLgpd
          .mutateAsync(customerId)
          .then(() => setRemind(!remind));
      }
    } catch {}
  };

  const handleClose = () => {
    notification.info({
      message: intl({ id: 'workOrders.notification.lgpd.information' }),
    });
    if (onCallback) {
      onCallback('close');
    }
    setModalShow.close();
    onClose();
    setAlert && setAlert(true);
  };

  return (
    <Modal
      open={modalShow}
      cancelText={intl({ id: 'commons.options.no' })}
      onCancel={handleClose}
      footer={
        <Space>
          <Button onClick={handleClose}>
            {intl({ id: 'commons.form.button.close' })}
          </Button>
          <Button
            onClick={() => handleEnableLgpd(false)}
            type="primary"
            danger
            disabled={customerLgpd.isLoading}
          >
            {intl({ id: 'commons.options.no' })}
          </Button>
          <SaveButton
            onClick={() => handleEnableLgpd(true)}
            disabled={customerLgpd.isLoading}
          >
            {intl({ id: 'commons.options.yes' })}
          </SaveButton>
        </Space>
      }
      title={
        <Space>
          <WarningTwoTone twoToneColor="red" />
          <Typography.Text>
            {intl({ id: 'workOrders.details.complement.modal.title' })}
          </Typography.Text>
        </Space>
      }
    >
      <Typography.Text>
        {intl({ id: 'workOrders.details.complement.modal.description' })}
      </Typography.Text>
      <Checkbox
        style={{ marginTop: '15px' }}
        checked={remind}
        onChange={handleDontShowAgain}
        disabled={customerShowLgpd.isLoading}
      >
        {intl({
          id: 'workOrders.details.complement.modal.checkbox',
        })}
      </Checkbox>
    </Modal>
  );
}
